import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui/buttons/Button';
import { TallyCountStepper } from '~/components/ui/TallyCountStepper';
import { RandomTallyBundlesGrid } from '~/components/common/RandomTallyBundlesGrid';
import {
  bundleCountsOf,
  bundleCountTexts,
  hasInvalidBundleCount,
  positiveBundleCounts,
  type TallyBundleCount,
} from '~/components/common/tallyBundleCounts';
import { useGetCannedTallyLengthsForCategory } from '~/api/canned-tally';
import type { BuyListDetailTally } from './types';

type EditorActionsProps = {
  canSave: boolean;
  onSave: () => void;
  onCancel: () => void;
  saveTestId: string;
  cancelTestId: string;
};

function EditorActions({
  canSave,
  onSave,
  onCancel,
  saveTestId,
  cancelTestId,
}: Readonly<EditorActionsProps>) {
  const { t } = useTranslation('inventory');
  return (
    <div className="flex items-center justify-end gap-2">
      <Button
        size="sm"
        width="auto"
        variant="secondary"
        onClick={onCancel}
        data-testid={cancelTestId}
      >
        {t('inventory.cancel')}
      </Button>
      <Button
        size="sm"
        width="auto"
        variant="primary"
        onClick={onSave}
        disabled={!canSave}
        data-testid={saveTestId}
      >
        {t('inventory.buy_list.tallies.save')}
      </Button>
    </div>
  );
}

type TallyCountEditorProps = {
  tallyCount: number;
  isSaving: boolean;
  onSave: (tallyCount: number) => void;
  onCancel: () => void;
};

export function TallyCountEditor({
  tallyCount: savedTallyCount,
  isSaving,
  onSave,
  onCancel,
}: Readonly<TallyCountEditorProps>) {
  const { t } = useTranslation('inventory');
  const inputId = useId();
  const [tallyCount, setTallyCount] = useState(savedTallyCount);

  return (
    <div className="flex w-60 flex-col gap-2 text-left">
      <label htmlFor={inputId} className="text-xs font-medium text-neut-sec">
        {t('inventory.buy_list.tallies.tally_count')}
      </label>
      <TallyCountStepper
        inputId={inputId}
        value={tallyCount}
        onChange={setTallyCount}
        disabled={isSaving}
      />
      <EditorActions
        canSave={tallyCount !== savedTallyCount && !isSaving}
        onSave={() => onSave(tallyCount)}
        onCancel={onCancel}
        saveTestId="buy-list-tally-count-save"
        cancelTestId="buy-list-tally-count-cancel"
      />
    </div>
  );
}

type RandomTallyEditorProps = {
  tally: BuyListDetailTally;
  isSaving: boolean;
  onSave: (bundles: TallyBundleCount[]) => void;
  onCancel: () => void;
};

export function RandomTallyEditor({
  tally,
  isSaving,
  onSave,
  onCancel,
}: Readonly<RandomTallyEditorProps>) {
  const [bundleCountsByLengthId, setBundleCountsByLengthId] = useState(() =>
    bundleCountTexts(bundleCountsOf(tally.bundles))
  );
  const { data: candidateLengthsData } = useGetCannedTallyLengthsForCategory(
    tally.platformProduct.id
  );
  const candidateLengths =
    (candidateLengthsData &&
      candidateLengthsData.cannedTallyLengthsForCategory) ??
    [];
  const candidateLengthIds = candidateLengths.map(({ length }) => length.id);
  const bundles = positiveBundleCounts(
    bundleCountsByLengthId,
    candidateLengthIds
  );
  const isValid =
    bundles.length > 0 &&
    !hasInvalidBundleCount(bundleCountsByLengthId, candidateLengthIds);

  return (
    <div
      className="flex flex-col gap-3 px-3 py-2"
      data-testid={`buy-list-tally-random-editor-${tally.id}`}
    >
      <RandomTallyBundlesGrid
        platformProductId={tally.platformProduct.id}
        bundleCountsByLengthId={bundleCountsByLengthId}
        onBundleCountChange={(lengthId, value) =>
          setBundleCountsByLengthId((current) => ({
            ...current,
            [lengthId]: value,
          }))
        }
        piecesPerBundle={tally.platformProduct.piecesPerBundle}
        disabled={isSaving}
      />
      <EditorActions
        canSave={isValid && !isSaving}
        onSave={() => onSave(bundles)}
        onCancel={onCancel}
        saveTestId={`buy-list-tally-bundles-save-${tally.id}`}
        cancelTestId={`buy-list-tally-bundles-cancel-${tally.id}`}
      />
    </div>
  );
}
